import React from 'react';
import { Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Grid2, Tooltip, Box } from '@mui/material';
import { ReactComponent as SelectedAiMessageText } from '../../assets/SVG/selectedAiMessageText.svg';
import { ReactComponent as AiMessageText } from '../../assets/SVG/aiMessageText.svg';
import { useTranslation } from 'react-i18next';
import { fetchEmbryoList, fetchEmbryoResult, getSelectedGradingHistory, deleteGradingHistory, GradingHistoryItem, Patient, handleNewAIGradingHelper, EditHistoryValues, updateSelectedGradingHistory } from './EmbryoGradingAi.helper';
import { useGradingHistory } from './GradingHistoryContext';
import { useAlert } from '../../components/alertProvider';
import HistoryMenuList from './HistoryMenuList';
import { ConfirmationModal } from '../../components/confirmationModal';
import RenameGradingHistoryModal from './RenameGradingHistoryModal';

const gradingLayoutSx = {
  bgcolor: '#f9fafb',
  height: '100%',
};

const gradingHeaderSx = {
  fontFamily: 'Montserrat',
  fontWeight: 400,
  fontSize: '14px',
  color: '#6E727F',
  mb: 1,
  py: 1,
};

// Accepts isSelected and isHovered
const getListItemSx = (isSelected: boolean, isHovered: boolean) => ({
  bgcolor: isSelected || isHovered ? '#FFFFFF' : 'transparent',
  borderRadius: 3,
  mb: 2,
  px: 2,
  py: 1,
  boxShadow: isSelected ? '0px 1px 4px rgba(0,0,0,0.04)' : 'none',
  cursor: 'pointer'
});

const getListItemTextSx = (isSelected: boolean) => ({
  fontFamily: 'Montserrat !important',
  fontWeight: isSelected ? 600 : 400,
  fontSize: '14px',
  color: isSelected ? '#F37A3B' : '#7B7B8B',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const ListItemIconSx = {
  minWidth: 36
};



const GradingResultsHistory: React.FC = () => {
  const { t: tAiGrading } = useTranslation('aiGrading_config');


  const { gradingHistoriesList , setSelectedGradingHistory , setGradingHistoriesList , loadingHistory, setSelectedPatientsForEmbryoGrading , setSelectedPatientForGrading , setSelectedEmbryosGrading , setSelectedEmbryoResult , setIsGradingGenerated , toggleResultAvailable , toggleEmbryoGradingDetailsOpen , selectedGradingHistory , setSelectedEmbryoForGrading } = useGradingHistory();


  const { addAlert } = useAlert();

  // Track hovered item id for showing menu on hover
  const [hoveredId, setHoveredId] = React.useState<number | null>(null);


  const [openModal, setOpenModal] = React.useState<boolean>(false);

  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);


  // get Embryo List and 1st Embryo Result for selected patient
  const getEmbryoList = (data: Patient) => {
    fetchEmbryoList(
      data,
      setSelectedEmbryosGrading,
      setSelectedEmbryoResult,
      addAlert,
      fetchEmbryoResult,
      setSelectedEmbryoForGrading,
      tAiGrading
    );
  };



  const handleNewAIGrading = () => {
  
    toggleResultAvailable(false); // start result available loader
    setIsGradingGenerated(false);  // start grade generation loader
  
    handleNewAIGradingHelper(
      toggleEmbryoGradingDetailsOpen,
      setSelectedPatientsForEmbryoGrading,
      setSelectedGradingHistory,
      setGradingHistoriesList
    );
  };


  const handleFetchSelectedHistory = async (historyId: number) => {
    try {
      getSelectedGradingHistory({ HistoryId: historyId }).then(async (response) => { 
        // const selectedGradingHistoryID = response?.id || null;
     
        // Map histories to selectedPatientsForEmbryoGrading
        if (response && Array.isArray(response.histories)) {
          const mappedPatients = response.histories.map((history: unknown, idx: number) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const h = history as any;
            const p = h.patient;
            return {
              id: p.id,
              name: [p.first_name, p.last_name].filter(Boolean).join(' '),
              mrn: p.medical_record_no,
              first_name: p.first_name,
              last_name: p.last_name,
              age: p.age,
              sex: p.gender__name,
              avatarUrl: p.photo,
              selected: idx === 0, // first patient selected
              gender: p.gender__name,
              allergy: null,
              lastModified: p.created_on,
              cycle_name: h.cycle_number || null,
              day: null,
              cycle_id: h.cycle || null,
              patient_history_id: h.id,
              is_result_available: h.is_result_available || false
            };
          });
      
      
          setSelectedPatientsForEmbryoGrading(mappedPatients); // set selected patients List
      
          if (mappedPatients.length > 0) {
            setSelectedPatientForGrading(mappedPatients[0]); // Set the individual selected patient
            if (mappedPatients[0].cycle_id && mappedPatients[0].is_result_available) {
              await getEmbryoList(mappedPatients[0]);
              toggleResultAvailable(true); // off the loader
              setIsGradingGenerated(true);   // set grading generated true
            } else {
              toggleResultAvailable(false); // keep loader off
              setIsGradingGenerated(false); 
            }
          }
      
        }
        
        toggleEmbryoGradingDetailsOpen(true); // show embryo grading details
      })
        .catch(() => {
          addAlert(tAiGrading('failedToFetchGradingHistory'), 'error');
          handleNewAIGrading(); // reset All for new AI grading
        });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      addAlert(tAiGrading('failedToFetchGradingHistory'), 'error');
      handleNewAIGrading(); // reset All for new AI grading
    }
  };






  const handleHistoryClick = (item: GradingHistoryItem ) => {

    const updated = gradingHistoriesList.map((p: GradingHistoryItem) =>
      p.id === item.id
        ? { ...p, isSelected: true }
        : { ...p, isSelected: false }
    );

    setGradingHistoriesList(updated);
    setSelectedGradingHistory(item); // Set the individual selected patient


    handleFetchSelectedHistory(item.id); // Fetch and set selected grading history details including patients and embryos

  };







  const handleRename = (value: EditHistoryValues) => {
    if (!hoveredId) return;
    updateSelectedGradingHistory(String(hoveredId), value)
      .then(() => {
        setGradingHistoriesList(prev => prev.map(item => item.id === hoveredId ? { ...item, name: value.name } : item));
        addAlert(tAiGrading('renameSuccess'), 'success');
      })
      .catch(() => {
        addAlert(tAiGrading('renameError'), 'error');
      });
  };



  const handleDelete = () => {
    if (hoveredId == null) return;
    deleteGradingHistory({ HistoryId: hoveredId })
      .then(() => {
        setGradingHistoriesList(prev => prev.filter((item: GradingHistoryItem) => item.id !== hoveredId));

        //if selected grading history is deleted then reset all for new AI grading
        if(selectedGradingHistory?.id === hoveredId){
          handleNewAIGrading(); // reset All for new AI grading
        }

        addAlert(tAiGrading('deletedHistorySuccess'), 'success');
      })
      .catch(() => {
        addAlert(tAiGrading('deletedHistoryError'), 'error');
      });
  };





  



  return (
    <Paper elevation={0} sx={gradingLayoutSx}>
      <Grid2 size={12} height={'100%'}>
        <Typography sx={{ ...gradingHeaderSx, fontFamily: 'Montserrat !important' }}>
          {tAiGrading('gradingResultsHistory')}
        </Typography>

        <Grid2 size={12} height={'calc(100vh - 395px)'} overflow={'auto'} pr={0.5}>
          <List disablePadding>
            {loadingHistory ? (
              <Typography variant="body2">{tAiGrading('loadingHistory')}</Typography>
            ) : gradingHistoriesList.length > 0 ? (
              gradingHistoriesList.map((item: GradingHistoryItem) => {
                const isHovered = hoveredId === item.id;
                return (
                  <ListItem
                    key={item.id}
                    sx={getListItemSx(item.isSelected, isHovered)}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    
                    <Box onClick={() => handleHistoryClick(item)} display={'flex'} alignItems={'center'} justifyContent={'space-between'} width={'90%'} >
                      <ListItemIcon sx={ListItemIconSx}>
                        {item.isSelected ? <SelectedAiMessageText /> : <AiMessageText />}
                      </ListItemIcon>
                      <Tooltip title={item.name} placement="top" arrow>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            sx: getListItemTextSx(item.isSelected)
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box>
                      {hoveredId === item.id && (
                        <>
                          <HistoryMenuList onRename={() => setEditModalOpen(true)} onDelete={() => setOpenModal(true)} />

                          <ConfirmationModal
                            open={openModal}
                            handleClose={() => setOpenModal(false)}
                            onConfirm={handleDelete}
                            type={'delete'}
                            title={tAiGrading('deleteGradingHistory')}
                            contentMessage={tAiGrading('deleteGradingHistoryConfirm')}
                          />



                          <RenameGradingHistoryModal
                            open={editModalOpen}
                            onClose={() => setEditModalOpen(false)}
                            onSave={handleRename}
                            initialValues={{ name: item.name }}
                          />

                        </>
                      
                      )}
                    </Box>
                  </ListItem>
                );
              })
            ) : (
              <Typography variant="body2">{tAiGrading('historyNotFound')}</Typography>
            )}
          </List>
        </Grid2>
      </Grid2>
    </Paper>
  );
};

export default GradingResultsHistory;
